import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Col,
  Button,
  Typography,
  Form,
  Input,
} from 'antd';
import Previewer from 'components/Previewer';
import { postObtenerDocumento } from 'api/reporteador';
import { ReloadOutlined } from '@ant-design/icons';
import { buildFileName, checkIfCanDownload } from 'utils/getFileName';
import { patchTramite } from 'api/tramites/tramites';
import NoPermission from './NoPermission';

// eslint-disable-next-line react/prop-types
const RetryError = ({ onClick }) => (
  <Button onClick={onClick}>
    <Typography.Text>
      Reintentar
    </Typography.Text>
    <ReloadOutlined />
  </Button>
);

const Documento = ({
  tramite,
  attrs,
  reporteID,
  setLoading,
  firmasElectronicas,
  hasPermission,
  iFrameUrl,
  disabled: _disabled,
  estados_globales,
  generarConsecutivo,
  capturarObservaciones,
}) => {
  const form = Form.useFormInstance();
  const base64 = Form.useWatch('base64', form);
  const [error, setError] = useState();
  const disabled = (_disabled && (base64 || iFrameUrl)) || estados_globales === 5;

  const data_reporte = useMemo(() => {
    if (!(tramite && reporteID)) {
      return null;
    }
    const _values = {
      ID: reporteID,
      Parametros: [
        {
          Nombre: 'tramite',
          TipoParametro: 'Texto',
          Valor: tramite.id,
        },
        {
          Nombre: 'clave',
          TipoParametro: 'Texto',
          Valor: tramite.id,
        },
        {
          Nombre: '__CodigoQR__',
          TipoParametro: 'Texto',
          Valor: `${process.env.REACT_APP_HOSTQR}/tramites/?folioTRM=${tramite.folio}&referencia_de_impresion=${tramite.referencia_de_impresion}`,
        },
      ],
    };
    Object.keys(attrs).forEach((attr) => {
      _values.Parametros.push({
        Nombre: attr,
        TipoParametro: 'Texto',
        Valor: attrs[attr],
      });
    });
    if (firmasElectronicas.length) {
      const cadena = firmasElectronicas[0]?.cadena;
      const firma_electronica = firmasElectronicas.map((f) => f.asString).join('\n')
        .concat(`CADENA ORIGINAL ${cadena}`);
      _values.Parametros.push({
        Nombre: 'firma_electronica',
        TipoParametro: 'Texto',
        Valor: firma_electronica,
      });
      firmasElectronicas.forEach((f, idx) => {
        _values.Parametros.push({
          Nombre: `firma_electronica_${idx + 1}`,
          TipoParametro: 'Texto',
          Valor: f,
        });
      });
      _values.Parametros.push({
        Nombre: 'cadena_original',
        TipoParametro: 'Texto',
        Valor: cadena,
      });
    }

    return _values;
  }, [tramite, reporteID]);

  const getDocumento = async (type) => {
    setLoading(true);
    if (generarConsecutivo) {
      const generado = await patchTramite(tramite.id, { generar_consecutivo: true });
      if (!generado) {
        setLoading(false);
        return;
      }
    }
    if (capturarObservaciones) {
      const bases_gravables_json = form.getFieldValue('observaciones');
      const generado = await patchTramite(tramite.id, { bases_gravables_json });
      if (!generado) {
        setLoading(false);
        return;
      }
    }
    const [err, _base64] = await postObtenerDocumento(data_reporte, type);
    form.setFieldsValue({ base64: _base64 });
    if (err) setError(err);
    setLoading(false);
  };

  useEffect(() => {
    if (!iFrameUrl && tramite && reporteID && !capturarObservaciones) {
      getDocumento('pdf');
    }
  }, []);

  if (!(tramite && reporteID)) return null;

  if (!hasPermission) return <NoPermission />;

  return (
    <>
      {!disabled && (
        <>
          {capturarObservaciones && (
            <Col span={24}>
              <Form.Item label="Observaciones" name="observaciones" rules={[{ required: true }]}>
                <Input.TextArea autoSize={{ minRows: 2, maxRows: 3 }} />
              </Form.Item>
            </Col>
          )}
          <Col span={24} style={{ marginBottom: 20 }}>
            <Button
              onClick={() => getDocumento('pdf')}
              style={{ float: 'right' }}
            >
              Generar documento PDF
            </Button>
            <Button
              onClick={() => getDocumento('excel')}
              style={{ float: 'right', marginRight: 5 }}
            >
              Generar documento XLSX
            </Button>
          </Col>
        </>
      )}
      <Previewer
        visible
        modal={false}
        base64={base64}
        iFrameUrl={iFrameUrl}
        error={error === 'retry' ? <RetryError error={error} onClick={getDocumento} /> : error}
        allowDownload={checkIfCanDownload(tramite)}
        customFileNameFormat={() => buildFileName(tramite)}
      />
      <Form.Item name="base64" rules={[{ required: !disabled }]} hidden><Input /></Form.Item>
    </>
  );
};

Documento.propTypes = {
  tramite: PropTypes.shape({
    attrs: PropTypes.shape({}),
    id: PropTypes.number,
    folio: PropTypes.string,
    referencia_de_impresion: PropTypes.string,
    descripcion: PropTypes.string,
    object_id_padron: PropTypes.number,
    content_type_a_padron: PropTypes.number,
    contribuyente: PropTypes.number,
  }),
  reporteID: PropTypes.number,
  setLoading: PropTypes.func.isRequired,
  firmasElectronicas: PropTypes.arrayOf(PropTypes.shape({
    cadena: PropTypes.string.isRequired,
    asString: PropTypes.string.isRequired,
  })),
  hasPermission: PropTypes.bool,
  attrs: PropTypes.shape({}),
  iFrameUrl: PropTypes.string,
  disabled: PropTypes.bool,
  estados_globales: PropTypes.number,
  generarConsecutivo: PropTypes.bool,
  capturarObservaciones: PropTypes.bool,
};

Documento.defaultProps = {
  tramite: null,
  reporteID: null,
  firmasElectronicas: [],
  hasPermission: false,
  attrs: {},
  iFrameUrl: null,
  disabled: false,
  estados_globales: null,
  generarConsecutivo: false,
  capturarObservaciones: false,
};

export default Documento;
